import {Button, Form, FormInstance, Input, Modal, Radio, Spin} from 'antd';
import {useEffect, useMemo} from 'react';

import {Locale} from '../../../../provider/locale/locale';
import {useLocale} from '../../../../provider/locale/locale-hook';
import {useSnackbar} from '../../../../provider/snackbar/snackbar-hook';
import {
    defaultExportConstructorLimits,
    useCreateExportTemplateMutation,
    useExportColumns,
    useExportLimits,
    useExportTemplate,
    useExportTemplates,
    useUpdateExportTemplateMutation,
} from '../../../../service/reviews/export-constructor/export-constructor-hook';

import {ExportTemplateAdditionalDataFields} from './components/export-template-additional-data-fields/export-template-additional-data-fields';
import {
    ExportTemplateColumnOptions,
    ExportTemplateColumnPicker,
} from './components/export-template-column-picker/export-template-column-picker';
import {ExportTemplatePreviewTable} from './components/export-template-preview-table/export-template-preview-table';
import {
    applyAddressDisplay,
    applyAuthorDisplay,
    buildEditorTemplateColumns,
    expandAddressDisplay,
    expandAuthorDisplay,
    exportConstructorEditorFormId,
    findParentTemplate,
    getEditorFormInitialValues,
    getEditorPickerColumns,
    isExportConstructorPivotColumnId,
    resolveParentTemplateId,
} from './reviews-export-constructor-helper';
import {
    ExportConstructorEditorFormEnum,
    ExportConstructorEditorFormType,
    ExportConstructorEditorStateType,
} from './reviews-export-constructor-types';
import * as styles from './reviews-export-template-editor-modal.module.scss';

type PropsType = {
    editorState: ExportConstructorEditorStateType | null;
    isOpen: boolean;
    onClose: () => void;
    onSaved: () => void;
};

// eslint-disable-next-line max-statements, complexity, sonarjs/cognitive-complexity
export function ReviewsExportTemplateEditorModal(props: PropsType): JSX.Element {
    const {editorState, isOpen, onClose, onSaved} = props;

    const {getLocalizedString} = useLocale();
    const {snackbar} = useSnackbar();
    const [form]: [FormInstance<ExportConstructorEditorFormType>] = Form.useForm();

    const watchedColumnIds = Form.useWatch(ExportConstructorEditorFormEnum.SelectedColumnIds, form);
    const selectedColumnIds = useMemo(() => watchedColumnIds ?? [], [watchedColumnIds]);
    const addressFormat = Form.useWatch(ExportConstructorEditorFormEnum.AddressFormat, form) ?? 'separate';
    const authorFormat = Form.useWatch(ExportConstructorEditorFormEnum.AuthorFormat, form) ?? 'single';

    const {data: apiColumns, isLoading: isCatalogLoading, isFetched: isColumnsFetched} = useExportColumns(isOpen);
    const {data: limits} = useExportLimits(isOpen);
    const {data: apiTemplates} = useExportTemplates(isOpen);

    const catalog = useMemo(() => apiColumns ?? [], [apiColumns]);

    const sourceTemplateId =
        editorState?.templateId ??
        (editorState?.sourceTemplateId && editorState.sourceTemplateId > 0 ? editorState.sourceTemplateId : null);
    const shouldFetchSourceTemplate = Boolean(sourceTemplateId) && !editorState?.sourceTemplate;

    const {data: fetchedSourceTemplate, isFetching: isTemplateFetching} = useExportTemplate(
        sourceTemplateId,
        isOpen && shouldFetchSourceTemplate
    );
    const sourceTemplate = editorState?.sourceTemplate ?? fetchedSourceTemplate;

    const {mutate: createTemplate, isLoading: isCreating} = useCreateExportTemplateMutation();
    const {mutate: updateTemplate, isLoading: isUpdating} = useUpdateExportTemplateMutation();

    const userTemplatesCount = (apiTemplates ?? []).filter((template) => !template.isSystem).length;

    const columnSourceTemplate = sourceTemplate;

    const parentTemplate = useMemo(() => {
        if (!columnSourceTemplate) {
            return null;
        }

        if (columnSourceTemplate.isSystem) {
            return columnSourceTemplate;
        }

        return findParentTemplate(columnSourceTemplate.parent, apiTemplates ?? []);
    }, [apiTemplates, columnSourceTemplate]);

    const columnsTemplate = parentTemplate ?? columnSourceTemplate;

    const pickerColumns = useMemo(() => {
        if (!columnsTemplate) {
            return [];
        }

        return getEditorPickerColumns(catalog, columnsTemplate);
    }, [catalog, columnsTemplate]);

    const pickerColumnIds = useMemo(() => pickerColumns.map((column) => column.id), [pickerColumns]);

    const parentColumnIds = useMemo(
        () => new Set(columnsTemplate?.columns.map((column) => column.id) ?? []),
        [columnsTemplate]
    );

    // Display-option blocks are shown only when the columns they format are actually
    // selected (checked), per the design annotations (CR-576 author, CR-479 address):
    // author → «Автор» checked; tags/autotags → their checkbox checked;
    // Улица/Дом merge → BOTH street and street_number checked.
    const hasTagsColumn = selectedColumnIds.includes('tags');
    const hasAutotagsColumn = selectedColumnIds.includes('autotags');
    const hasReviewsSummaryPivot = parentColumnIds.has('reviews_summary_pivot');
    const hasTagsStatPivot = parentColumnIds.has('tags_pivot');
    const hasAddressColumns = selectedColumnIds.includes('street') && selectedColumnIds.includes('street_number');
    const hasAuthorColumn = selectedColumnIds.includes('author');

    // Preview reflects the display swaps (author → 3 columns, Улица/Дом → «Адрес»);
    // the full catalog resolves the synthetic column labels.
    const previewColumnIds = useMemo(
        () => applyAddressDisplay(applyAuthorDisplay(selectedColumnIds, authorFormat), addressFormat),
        [addressFormat, authorFormat, selectedColumnIds]
    );

    const isEditMode = editorState?.mode === 'edit';
    const isLimitReached =
        !isEditMode && userTemplatesCount >= (limits ?? defaultExportConstructorLimits).maxUserTemplates;

    const isEditorLoading =
        (isCatalogLoading && !isColumnsFetched) || (shouldFetchSourceTemplate && isTemplateFetching);

    useEffect(() => {
        if (!isOpen || !editorState) {
            return;
        }

        if (pickerColumnIds.length === 0) {
            return;
        }

        if (shouldFetchSourceTemplate && isTemplateFetching) {
            return;
        }

        form.setFieldsValue(
            getEditorFormInitialValues({
                isEditMode,
                availableColumnIds: pickerColumnIds,
                columnSourceTemplate,
            })
        );
    }, [
        columnSourceTemplate,
        editorState,
        form,
        isEditMode,
        isOpen,
        isTemplateFetching,
        pickerColumnIds,
        shouldFetchSourceTemplate,
    ]);

    function handleSaveSuccess() {
        onSaved();
        onClose();
    }

    // eslint-disable-next-line complexity
    function handleSubmit(formValues: ExportConstructorEditorFormType) {
        const {
            name,
            selectedColumnIds: columnIds,
            tagsFormat,
            autotagsFormat,
            addressFormat: submittedAddressFormat,
            authorFormat: submittedAuthorFormat,
            withReviewsSummary,
            withTagsStat,
        } = formValues;

        if (columnIds.length === 0) {
            snackbar.error(<Locale stringKey="REVIEWS__EXPORT_CONSTRUCTOR__COLUMNS_REQUIRED" />);

            return;
        }

        const finalColumns = buildEditorTemplateColumns({
            selectedColumnIds: columnIds.filter((columnId) => !isExportConstructorPivotColumnId(columnId)),
            tagsFormat,
            autotagsFormat,
            addressFormat: submittedAddressFormat ?? 'separate',
            authorFormat: submittedAuthorFormat ?? 'single',
            withReviewsSummary: hasReviewsSummaryPivot ? withReviewsSummary : false,
            withTagsStat: hasTagsStatPivot ? withTagsStat : false,
        });

        if (isEditMode && editorState?.templateId) {
            updateTemplate(
                {
                    templateId: editorState.templateId,
                    payload: {name, columns: finalColumns},
                },
                {onSuccess: handleSaveSuccess}
            );

            return;
        }

        if (isLimitReached) {
            snackbar.error(<Locale stringKey="REVIEWS__EXPORT_CONSTRUCTOR__LIMIT_REACHED" />);

            return;
        }

        const parentId = columnSourceTemplate ? resolveParentTemplateId(columnSourceTemplate) : null;

        if (!parentId) {
            snackbar.error(getLocalizedString('REVIEWS__DOWNLOAD_REPORT_POPUP__THE_APPLICATION_ERROR_SENT'));

            return;
        }

        createTemplate({name: name.trim(), columns: finalColumns, parent: parentId}, {onSuccess: handleSaveSuccess});
    }

    const titleStringKey = isEditMode
        ? 'REVIEWS__EXPORT_CONSTRUCTOR__EDIT_TITLE'
        : 'REVIEWS__EXPORT_CONSTRUCTOR__CREATE_TITLE';

    return (
        <Modal
            className={styles.ReviewsExportTemplateEditorModal}
            destroyOnClose
            footer={[
                <Button key="cancel" onClick={onClose}>
                    <Locale stringKey="POPUP__BUTTON__CANCEL" />
                </Button>,
                <Button
                    disabled={isLimitReached}
                    form={exportConstructorEditorFormId}
                    htmlType="submit"
                    key="submit"
                    loading={isCreating || isUpdating}
                    type="primary"
                >
                    <Locale stringKey="REVIEWS__EXPORT_CONSTRUCTOR__BUTTON__SAVE" />
                </Button>,
            ]}
            onCancel={onClose}
            open={isOpen}
            title={<Locale stringKey={titleStringKey} />}
            width="1000px"
        >
            <Spin spinning={isEditorLoading}>
                <Form<ExportConstructorEditorFormType>
                    form={form}
                    id={exportConstructorEditorFormId}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Form.Item<ExportConstructorEditorFormType>
                        label={<Locale stringKey="REVIEWS__DOWNLOAD_REPORT_POPUP__NAME__LABEL__TITLE" />}
                        name={ExportConstructorEditorFormEnum.Name}
                        rules={[
                            {
                                required: true,
                                message: getLocalizedString('REVIEWS__EXPORT_CONSTRUCTOR__NAME_REQUIRED'),
                            },
                        ]}
                    >
                        <Input placeholder={getLocalizedString('REVIEWS__EXPORT_CONSTRUCTOR__NAME_PLACEHOLDER')} />
                    </Form.Item>

                    <div className={styles.ReviewsExportTemplateEditorModal_section}>
                        <div className={styles.ReviewsExportTemplateEditorModal_sectionTitle}>
                            <Locale stringKey="REVIEWS__EXPORT_CONSTRUCTOR__COLUMNS_LABEL" />
                        </div>
                        <ExportTemplateColumnPicker catalog={pickerColumns} />
                    </div>

                    {hasAuthorColumn ? (
                        <Form.Item<ExportConstructorEditorFormType>
                            label={<Locale stringKey="REVIEWS__EXPORT_CONSTRUCTOR__AUTHOR_FORMAT_LABEL" />}
                            name={ExportConstructorEditorFormEnum.AuthorFormat}
                        >
                            <Radio.Group>
                                <Radio value="single">
                                    <Locale stringKey="REVIEWS__EXPORT_CONSTRUCTOR__AUTHOR_FORMAT_SINGLE" />
                                </Radio>
                                <Radio value="separate">
                                    <Locale stringKey="REVIEWS__EXPORT_CONSTRUCTOR__AUTHOR_FORMAT_SEPARATE" />
                                </Radio>
                            </Radio.Group>
                        </Form.Item>
                    ) : null}

                    {hasTagsColumn ? (
                        <ExportTemplateColumnOptions
                            labelStringKey="REVIEWS__EXPORT_CONSTRUCTOR__TAGS_FORMAT_LABEL"
                            name={ExportConstructorEditorFormEnum.TagsFormat}
                        />
                    ) : null}

                    {hasAutotagsColumn ? (
                        <ExportTemplateColumnOptions
                            labelStringKey="REVIEWS__EXPORT_CONSTRUCTOR__AUTOTAGS_FORMAT_LABEL"
                            name={ExportConstructorEditorFormEnum.AutotagsFormat}
                        />
                    ) : null}

                    {hasAddressColumns ? (
                        <Form.Item<ExportConstructorEditorFormType>
                            label={<Locale stringKey="REVIEWS__EXPORT_CONSTRUCTOR__ADDRESS_FORMAT_LABEL" />}
                            name={ExportConstructorEditorFormEnum.AddressFormat}
                        >
                            <Radio.Group>
                                <Radio value="merged">
                                    <Locale stringKey="REVIEWS__EXPORT_CONSTRUCTOR__ADDRESS_FORMAT_MERGED" />
                                </Radio>
                                <Radio value="separate">
                                    <Locale stringKey="REVIEWS__EXPORT_CONSTRUCTOR__ADDRESS_FORMAT_SEPARATE" />
                                </Radio>
                            </Radio.Group>
                        </Form.Item>
                    ) : null}

                    {hasReviewsSummaryPivot || hasTagsStatPivot ? (
                        <div className={styles.ReviewsExportTemplateEditorModal_section}>
                            <ExportTemplateAdditionalDataFields<ExportConstructorEditorFormType>
                                reviewsSummaryName={ExportConstructorEditorFormEnum.WithReviewsSummary}
                                showReviewsSummary={hasReviewsSummaryPivot}
                                showTagsStat={hasTagsStatPivot}
                                tagsStatName={ExportConstructorEditorFormEnum.WithTagsStat}
                            />
                        </div>
                    ) : null}

                    <div className={styles.ReviewsExportTemplateEditorModal_section}>
                        <ExportTemplatePreviewTable
                            catalog={catalog}
                            onReorder={(orderedPreviewIds) => {
                                form.setFieldValue(
                                    ExportConstructorEditorFormEnum.SelectedColumnIds,
                                    expandAuthorDisplay(
                                        expandAddressDisplay(orderedPreviewIds, selectedColumnIds),
                                        selectedColumnIds
                                    )
                                );
                            }}
                            selectedColumnIds={previewColumnIds}
                        />
                    </div>
                </Form>
            </Spin>
        </Modal>
    );
}
